import React, { useEffect, useMemo, useRef, useState } from 'react';
import { robustezzaPassword } from '../../utils/registrazione';
import { PRINCIPALE, erroreIn, traduci, useTraduzioni } from '../../i18n';

// Pezzi comuni delle card di accesso e registrazione (stile in accesso.css).
// Questi pezzi stanno dentro <ScenaAccesso>, che mette la lingua dell'ospite (FornitoreLingua): useTraduzioni()
// qui dà quella del browser (o quella del profilo, se si è già entrati).

const ATTESA_RINVIO = 30; // secondi prima di poter chiedere un altro codice
const VUOTE = ['', '', '', '', '', ''];

const classi = (...nomi) => nomi.filter(Boolean).join(' ');

const conPunto = (testo) => (/[.!?]$/.test(testo) ? testo : `${testo}.`);

// Server che non risponde (rete assente, 500, 502 del proxy, 503 durante un rilascio): si invita a riprovare
const SERVIZIO_ASSENTE = traduci(PRINCIPALE, 'comune.servizio_assente');
const servizioAssente = (error) => !error?.response || error.response.status >= 500;

// Messaggio da mostrare nella card per una risposta di errore del backend (mai un detail che non sia testo).
// Il backend risponde sempre in italiano e non si tocca: il detail passa da erroreIn (sezione "errori" dei
// dizionari, dove la chiave è proprio il testo italiano); se la traduzione non c'è resta l'italiano com'è.
// lingua = quella del cliente (useTraduzioni().l). Senza lingua è tutto in italiano, come prima.
const messaggioErrore = (error, ripiego, lingua = PRINCIPALE) => {
  if (servizioAssente(error)) return traduci(lingua, 'comune.servizio_assente');
  const dettaglio = error.response.data?.detail;
  return conPunto(typeof dettaglio === 'string' && dettaglio.trim() ? erroreIn(lingua, dettaglio) : ripiego);
};

// Limite di invii del codice raggiunto (il server risponde { ok: true, limite: true } e non manda niente)
const TROPPI_CODICI = traduci(PRINCIPALE, 'accesso.troppi_codici');

// Il nome del riconoscimento del dispositivo per i testi: i marchi restano (Face ID, Touch ID, Windows Hello),
// l'impronta si traduce. piattaforma() sta in utils/passkey.js e parla italiano.
const nomeSistema = (sistema, t) => (sistema === 'impronta' ? t('accesso.impronta')
  : sistema === 'Face ID / impronta' ? t('accesso.impronta_o_volto') : sistema);

// Un invio alla volta: doppio clic, o clic e poi Invio con la rete lenta, non mandano due richieste.
// blocca() dà false se un invio è già in corso; sblocca() dopo un errore. Dopo un accesso riuscito si resta
// bloccati: la card sta uscendo.
const useInvioUnico = () => {
  const inCorso = useRef(false);
  return useMemo(() => ({
    blocca: () => {
      if (inCorso.current) return false;
      inCorso.current = true;
      return true;
    },
    sblocca: () => { inCorso.current = false; },
  }), []);
};

const minutiSecondi = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const Freccia = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

const Occhio = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>
);

// Icone lineari per l'accesso rapido: volto (Face ID, Windows Hello) o impronta
const IconaRapido = ({ tipo }) => (tipo === 'volto' ? (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2" /><path d="M9 9.5v1M15 9.5v1M12 9.5V13h-1M9.5 15.5c1.4 1.2 3.6 1.2 5 0" /></svg>
) : (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.8 5.6A8 8 0 0 1 20 12v1.5M4 12a8 8 0 0 1 1.1-4M8 12a4 4 0 0 1 8 0c0 2.5-.5 5-1.5 7M12 12c0 3-.6 6-2 8.5M4.5 16c.3-1.2.5-2.6.5-4M7.5 19.5c.8-1.8 1.2-4 1.2-6M19.5 17c.2-1 .4-2.2.5-3.5" /></svg>
));

// Testa della card: saluto (o altra etichetta), titolo con una parte in corsivo oro, sottotitolo
const TestaCard = ({ etichetta, titolo, corsivo, dopo, sotto }) => (
  <div className="head">
    <div className="hello">{etichetta}</div>
    <h1>{titolo} <em>{corsivo}</em>{dopo}</h1>
    {sotto && <p className="sub">{sotto}</p>}
  </div>
);

// Messaggio nella card: errore (rosso, con scossone) oppure conferma (verde)
const Avviso = ({ avviso }) => (
  <div className={classi('err', avviso && 'on', avviso?.ok && 'ok')} role={avviso && !avviso.ok ? 'alert' : 'status'}>
    {avviso?.testo}
  </div>
);

// Bottone oro con freccia e rotellina. In attesa è anche disabled: pointer-events ferma solo il clic, mentre con
// il bottone disabilitato il browser non invia il modulo nemmeno con il tasto Invio.
const Bottone = ({ attesa, children, freccia = true, className, disabled, ...resto }) => (
  <button type="submit" className={classi('btn', attesa && 'busy', className)} aria-busy={!!attesa}
    disabled={!!attesa || !!disabled} {...resto}>
    <span>{children}</span>{freccia && <Freccia />}
  </button>
);

// Campo incassato con etichetta che sale (React 19: ref arriva come prop e va all'input)
const Campo = ({ id, etichetta, valore, onChange, invalido, classe, type = 'text', ...resto }) => (
  <div className={classi('fl', classe)}>
    <input id={id} type={type} placeholder=" " value={valore} onChange={(e) => onChange(e.target.value)}
      aria-invalid={invalido || undefined} {...resto} />
    <label htmlFor={id}>{etichetta}</label>
  </div>
);

// Campo password con l'occhio per mostrarla. Le password nuove (autoComplete="new-password": registrazione, reset)
// al massimo 72 caratteri (MAX_BYTE_PASSWORD); quella di adesso no (le vecchie più lunghe entrano come prima)
const CampoPassword = ({ id, etichetta, valore, onChange, visibile, onVisibile, invalido, ...resto }) => {
  const { t } = useTraduzioni();
  return (
    <div className="fl">
      <input id={id} type={visibile ? 'text' : 'password'} placeholder=" " value={valore}
        maxLength={resto.autoComplete === 'new-password' ? MAX_BYTE_PASSWORD : undefined}
        onChange={(e) => onChange(e.target.value)} aria-invalid={invalido || undefined} {...resto} />
      <label htmlFor={id}>{etichetta || t('accesso.password')}</label>
      {onVisibile && (
        <button type="button" className="eye" aria-label={t(visibile ? 'accesso.nascondi_password' : 'accesso.mostra_password')}
          aria-pressed={!!visibile} onClick={onVisibile}><Occhio /></button>
      )}
    </div>
  );
};

// Robustezza della password: quattro tacche e "Debole / Buona / Ottima"
const IndicatoreRobustezza = ({ password }) => {
  const { t, l } = useTraduzioni();
  const { tacche, livello, testo } = robustezzaPassword(password, l);
  const regola = t('accesso.regola_password');
  return (
    <div className={classi('forza', `forza-${livello}`, !password && 'vuota')} aria-live="polite">
      <div className="tacche" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => <i key={i} className={i < tacche ? 'piena' : undefined} />)}
      </div>
      <span>{password ? testo : `${regola[0].toUpperCase()}${regola.slice(1)}`}</span>
    </div>
  );
};

// Casella obbligatoria per privacy e termini (i link si aprono in un'altra scheda: il modulo resta). ?da=: nella
// scheda nuova "Torna alla registrazione" (prima "Torna al sito" portava al login)
const CasellaPrivacy = ({ spuntata, onChange, invalida, ref }) => {
  const { t } = useTraduzioni();
  const da = `?da=${encodeURIComponent(window.location.pathname)}`;
  return (
    <label className="spunta" aria-invalid={invalida || undefined}>
      <input type="checkbox" ref={ref} checked={spuntata} onChange={(e) => onChange(e.target.checked)} data-testid="register-privacy" />
      <i />
      <span>
        {t('accesso.ho_letto')} <a href={`/privacy${da}`} target="_blank" rel="noopener noreferrer">{t('legali.privacy_breve')}</a>{' '}
        {t('accesso.e_accetto')} <a href={`/termini${da}`} target="_blank" rel="noopener noreferrer">{t('legali.termini')}</a>
      </span>
    </label>
  );
};

// Password nuove: bcrypt usa solo i primi 72 byte (una lettera accentata ne vale due). maxLength ferma i caratteri,
// questa funzione i byte (per le lettere accentate)
const MAX_BYTE_PASSWORD = 72;
const PASSWORD_LUNGA = traduci(PRINCIPALE, 'accesso.password_lunga');
const byteUtf8 = (testo) => Array.from(String(testo || '')).reduce((n, c) => {
  const p = c.codePointAt(0);
  return n + (p < 0x80 ? 1 : p < 0x800 ? 2 : p < 0x10000 ? 3 : 4);
}, 0);
const passwordTroppoLunga = (password) => byteUtf8(password) > MAX_BYTE_PASSWORD;

const Rinvia = ({ secondi, onClick }) => {
  const { t } = useTraduzioni();
  return secondi > 0
    ? <span className="attesa">{t('accesso.rinvia_tra', { tempo: minutiSecondi(secondi) })}</span>
    : <a href="#rinvia" role="button" onClick={(e) => { e.preventDefault(); onClick(); }}>{t('accesso.rinvia')}</a>;
};

// Conto alla rovescia in secondi (per "Rinvia")
const useContoAllaRovescia = () => {
  const [secondi, setSecondi] = useState(0);
  useEffect(() => {
    if (secondi <= 0) return undefined;
    const t = setTimeout(() => setSecondi((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondi]);
  return [secondi, () => setSecondi(ATTESA_RINVIO)];
};

// Sei caselle per il codice: avanzano da sole, accettano l'incolla (e il riempimento automatico
// del telefono), backspace torna alla casella precedente
// Le cifre scritte con la tastiera si gestiscono nel keydown: scrivendo sopra una casella piena la stessa cifra
// (482917 → clic sulla prima → 482971, o lo stesso codice dopo un errore) il valore non cambiava, React non chiamava
// onChange e il fuoco restava fermo: partiva 829717. Con tutte le caselle già piene il codice parte quando si scrive
// l'ultima cifra (prima partiva al primo tasto, con la prima cifra nuova e le altre vecchie).
const CodiceSeiCifre = ({ cifre, onChange, onCompleto, caselle }) => {
  const { t } = useTraduzioni();
  const imposta = (nuove, fuoco, invia = !cifre.every(Boolean)) => {
    onChange(nuove);
    if (fuoco != null) caselle.current[fuoco]?.focus();
    if (onCompleto && invia && nuove.every(Boolean)) onCompleto(nuove.join(''));
  };

  // una cifra nella casella i: la scrive (anche se è la stessa di prima) e passa alla successiva
  const scrivi = (i, c) => {
    const nuove = [...cifre];
    nuove[i] = c;
    imposta(nuove, i < 5 ? i + 1 : null, !cifre.every(Boolean) || i === 5);
  };

  const cambia = (i, valore) => {
    let d = valore.replace(/\D/g, '');
    // cifra scritta sopra a quella che c'era già
    if (d.length === 2 && cifre[i]) d = d[0] === cifre[i] ? d[1] : d[0];
    if (d.length > 1) {
      if (d.length >= 6) { imposta(d.slice(0, 6).split(''), 5, true); return; } // codice intero (riempimento del telefono): parte
      const nuove = [...cifre];
      let k = i;
      for (const c of d) { if (k > 5) break; nuove[k] = c; k += 1; }
      imposta(nuove, Math.min(k, 5));
      return;
    }
    if (d) { scrivi(i, d); return; }
    const nuove = [...cifre];
    nuove[i] = d;
    imposta(nuove, null);
  };

  // Tastiere del telefono senza il tasto nel keydown ("Unidentified"): la stessa cifra riscritta non cambia il valore
  // e non arriva a onChange; l'evento input sì. Si passa alla casella dopo come se fosse cambiata.
  const input = (i, e) => {
    if (e.target.value && e.target.value === cifre[i] && /^\d$/.test(e.target.value)) scrivi(i, cifre[i]);
  };

  const tasto = (i, e) => {
    if (/^\d$/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      scrivi(i, e.key);
    } else if (e.key === 'Backspace' && !cifre[i] && i > 0) {
      e.preventDefault();
      const nuove = [...cifre];
      nuove[i - 1] = '';
      onChange(nuove);
      caselle.current[i - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      caselle.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < 5) {
      e.preventDefault();
      caselle.current[i + 1]?.focus();
    }
  };

  const incolla = (e) => {
    const d = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (d.length >= 6) {
      e.preventDefault();
      imposta(d.slice(0, 6).split(''), 5, true);
    }
  };

  return (
    <div className="otp">
      {cifre.map((c, i) => (
        <input
          key={i}
          ref={(el) => { caselle.current[i] = el; }}
          value={c}
          className={c ? 'ok' : undefined}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={t('accesso.cifra_di_sei', { n: i + 1 })}
          onChange={(e) => cambia(i, e.target.value)}
          onInput={(e) => input(i, e)}
          onKeyDown={(e) => tasto(i, e)}
          onPaste={incolla}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
};

// Interruttore "Resta collegato": vale per password, codice via email e accesso rapido
const RestaCollegato = ({ acceso, onChange }) => {
  const { t } = useTraduzioni();
  return (
    <label className="sw">
      <input type="checkbox" checked={acceso} onChange={(e) => onChange(e.target.checked)} data-testid="resta-collegato" />
      <i />{t('accesso.resta_collegato')}
    </label>
  );
};

export {
  VUOTE, SERVIZIO_ASSENTE, TROPPI_CODICI, classi, conPunto, servizioAssente, messaggioErrore, nomeSistema, useInvioUnico,
  Freccia, Occhio, IconaRapido, TestaCard, Avviso, Bottone, Campo, CampoPassword, IndicatoreRobustezza, CasellaPrivacy,
  RestaCollegato, Rinvia, useContoAllaRovescia, CodiceSeiCifre, MAX_BYTE_PASSWORD, PASSWORD_LUNGA, passwordTroppoLunga,
};
